/**
 * Production-grade streaming hook - ChatGPT style
 *
 * Uses direct DOM manipulation during streaming for maximum performance.
 * No React re-renders until streaming completes.
 *
 * @module useStreamingMessage
 */

import {useRef, useState, useCallback} from 'react';

export function useStreamingMessage() {
    const elementRef = useRef(null);
    const textRef = useRef('');
    const [isComplete, setIsComplete] = useState(false);
    const [queryTime, setQueryTime] = useState(null);

    /**
     * Append text delta to message
     * Uses direct DOM manipulation - NO React re-render
     */
    const appendText = useCallback((delta) => {
        textRef.current += delta;

        // Direct DOM update (bypasses React)
        if (elementRef.current) {
            elementRef.current.textContent = textRef.current;
        }
    }, []);

    /**
     * Mark message as complete
     * Triggers ONE React re-render to show rich markdown
     */
    const complete = useCallback((timing) => {
        setIsComplete(true);
        if (timing) {
            setQueryTime(timing.query_time);
        }
    }, []);

    /**
     * Reset message state
     */
    const reset = useCallback(() => {
        textRef.current = '';
        setIsComplete(false);
        setQueryTime(null);
        if (elementRef.current) {
            elementRef.current.textContent = '';
        }
    }, []);

    return {
        elementRef,
        textRef,
        isComplete,
        queryTime,
        appendText,
        complete,
        reset
    };
}

export default useStreamingMessage;
